import { UNDEFINED_LENGTH, makeTag } from './tag';
import {
    EXPLICIT_VR_BIG_ENDIAN,
    EXPLICIT_VR_LITTLE_ENDIAN,
    IMPLICIT_VR_LITTLE_ENDIAN,
    TransferSyntax,
    transferSyntaxFor,
} from './transferSyntax';
import { isVr, usesLongFormLength } from './vr';

/**
 * Determining how a dataset is encoded when nothing declares it.
 *
 * PS3.10 puts the transfer syntax in the file meta group, and a file carrying
 * one is always read under what it names. A dataset stored without that group
 * — written straight off a network association, or produced by a tool that
 * emits the dataset alone — still has to be read, and the only evidence left
 * is the element stream itself.
 *
 * That evidence is taken from structure, not from content. A candidate syntax
 * is accepted when the first few elements walk cleanly under it: ascending
 * tags, a representation the standard defines, and value lengths that are even
 * and stay inside the buffer. Judging instead by what values look like — that
 * a UID is digits and dots, that a code string is upper case — accepts a
 * syntax the stream cannot actually be read under, and reading a dataset under
 * the wrong syntax does not fail. It yields reversed or shifted numbers for
 * geometry, spacing and pixel data: a plausible image of the wrong patient
 * geometry rather than an error anyone would notice.
 */

/**
 * The syntaxes a headerless dataset can be in, in the order a tie is resolved.
 *
 * Explicit VR is tried first because it is self-describing: a stream that
 * walks under it carries the representations it claims, whereas any byte
 * sequence at all can be read as implicit VR if the lengths happen to land.
 * The retired big-endian syntax is detected so that a file in it is named
 * rather than misread, though the reader goes on to refuse it.
 */
const CANDIDATE_SYNTAXES: readonly TransferSyntax[] = [
    transferSyntaxFor(EXPLICIT_VR_LITTLE_ENDIAN),
    transferSyntaxFor(IMPLICIT_VR_LITTLE_ENDIAN),
    transferSyntaxFor(EXPLICIT_VR_BIG_ENDIAN),
];

/** How far a walk goes before a syntax is considered established. */
const ELEMENTS_TO_WALK = 4;

/**
 * How many elements must walk before a syntax is believed.
 *
 * One proves nothing: a single element's length field can land inside the
 * buffer by chance. Two elements means the first one's length was right, since
 * a wrong length puts the second tag in the middle of a value where it is very
 * unlikely to look like a tag at all.
 */
const ELEMENTS_TO_BELIEVE = 2;

/**
 * The syntax a dataset beginning at `offset` is encoded in, or undefined when
 * the bytes are not a DICOM element stream at all.
 */
export function detectTransferSyntax(
    bytes: Uint8Array,
    offset: number
): TransferSyntax | undefined {
    let best: TransferSyntax | undefined;
    let longestWalk = 0;

    for (const candidate of CANDIDATE_SYNTAXES) {
        const walked = walkableElements(bytes, offset, candidate);
        if (walked > longestWalk) {
            best = candidate;
            longestWalk = walked;
        }
    }

    return longestWalk >= ELEMENTS_TO_BELIEVE ? best : undefined;
}

/**
 * How many consecutive elements read cleanly from `offset` under `syntax`,
 * counting at most ELEMENTS_TO_WALK.
 */
function walkableElements(
    bytes: Uint8Array,
    offset: number,
    syntax: TransferSyntax
): number {
    const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);

    let at = offset;
    let previousTag = -1;
    let walked = 0;

    while (walked < ELEMENTS_TO_WALK) {
        if (at + 8 > view.byteLength) {
            return walked;
        }

        const group = view.getUint16(at, syntax.littleEndian);
        const element = view.getUint16(at + 2, syntax.littleEndian);
        const tag = makeTag(group, element);

        // Group 0000 is the command set and group 0002 the file meta, and
        // PS3.10 puts neither in the dataset a file stores. Meeting one here
        // means the tags are being read at the wrong offset or in the wrong
        // byte order.
        if (group === 0x0000 || group === 0x0002) {
            return walked;
        }
        // PS3.5 requires ascending tag order, so a tag that does not advance
        // is a value being read as a header.
        if (tag <= previousTag) {
            return walked;
        }

        const value = valueExtent(view, at, syntax);
        if (value === undefined) {
            return walked;
        }

        walked++;
        if (value.length === UNDEFINED_LENGTH) {
            // A sequence or encapsulated pixel data: where it ends is only
            // known by reading it, which is the reader's job and not a
            // detector's. The element itself parsed, so it counts.
            return walked;
        }

        previousTag = tag;
        at = value.at + value.length;
    }

    return walked;
}

interface ValueExtent {
    /** Offset of the first value byte. */
    readonly at: number;
    readonly length: number;
}

/**
 * Where the value of the element at `at` starts and how long it claims to be,
 * or undefined when the bytes there cannot be an element header.
 */
function valueExtent(
    view: DataView,
    at: number,
    syntax: TransferSyntax
): ValueExtent | undefined {
    if (!syntax.explicitVr) {
        return checked(
            view,
            at + 8,
            view.getUint32(at + 4, syntax.littleEndian)
        );
    }

    const vr = String.fromCharCode(
        view.getUint8(at + 4),
        view.getUint8(at + 5)
    );
    if (!isVr(vr)) {
        return undefined;
    }
    if (usesLongFormLength(vr)) {
        if (at + 12 > view.byteLength) {
            return undefined;
        }
        // The two bytes between the representation and the long length are
        // reserved and set to zero; anything else is not this layout.
        if (view.getUint16(at + 6, syntax.littleEndian) !== 0) {
            return undefined;
        }
        return checked(
            view,
            at + 12,
            view.getUint32(at + 8, syntax.littleEndian)
        );
    }
    return checked(view, at + 8, view.getUint16(at + 6, syntax.littleEndian));
}

/**
 * A value extent that stays inside the buffer and is of even length as PS3.5
 * requires, or undefined.
 *
 * The even-length rule is what separates the two little-endian syntaxes when
 * a stream could otherwise be read as either: every representation that uses
 * the long-form length begins with O, S or U, all of which are odd bytes, so
 * an explicit-VR header read as an implicit-VR length is always odd and is
 * rejected here rather than being resolved by preference.
 */
function checked(
    view: DataView,
    at: number,
    length: number
): ValueExtent | undefined {
    if (length === UNDEFINED_LENGTH) {
        return { at, length };
    }
    if (length % 2 !== 0 || at + length > view.byteLength) {
        return undefined;
    }
    return { at, length };
}
